import React, { useState } from 'react';
import { IconMail } from '@amzn/stencil-react-components/icons';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import {useGlobalContext, useUserDataContext} from '../../hooks';
import { Text } from '@amzn/stencil-react-components/text';
import { Flex } from '@amzn/stencil-react-components/layout';
import { WithModal, ModalContent } from '@amzn/stencil-react-components/modal';
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import {WA_WAREHOUSE_ALLOW_LIST, REPORTING_WAREHOUSE_ALLOW_LIST} from "src/constants/featureGates";

interface EmailReportWidgetProps {
  reportType: string;
}

export const EmailReportWidget = (props: EmailReportWidgetProps) => {
  const { emailReport } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  const [sendEmailAttempted, setSendEmailAttempted] = useState(false);
  const [sendEmailResponse, setSendEmailResponse] = useState(undefined as string|undefined);
  const [sendEmailFailed, setSendEmailFailed] = useState(false);

  const sendEmailSucceeded = sendEmailAttempted && sendEmailResponse != undefined;
  const sendEmailCompleted = sendEmailSucceeded || sendEmailFailed;
  const sendEmailInProgress = sendEmailAttempted && !sendEmailCompleted;

  const sendEmail = async (close: () => void) => {
    setSendEmailAttempted(true);
    close();

    try {
      const response = await emailReport(props.reportType);

      setSendEmailResponse(response);
    } catch (error) {
      setSendEmailFailed(true);
    }
  }

  const failureBanner =
    <MessageBanner isDismissible type={MessageBannerType.Error} titleText='Error'>
      Sorry, but we were unable to send an email. We are looking into this issue. Please try again later.
    </MessageBanner>;

  const successBanner =
    <MessageBanner isDismissible type={MessageBannerType.Success} titleText='Success'>
      {`Email successfully sent to ${sendEmailResponse}.`}
    </MessageBanner>;

  const renderModal = ({ close }: { close: () => void }) => {
    let reportType = 'your full report';
    if (props.reportType === 'FourteenWeekProductivityReport') {
      if (WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId)) {
        reportType = 'your Amazon 26-Week Productivity Summary';
      } else {
        reportType = 'your Amazon 14-Week Productivity Summary';
      }
    } else if (props.reportType === 'FourteenWeekQualityReport') {
      if (WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId)) {
        reportType = 'your Amazon 26-Week Quality Summary';
      } else {
        reportType = 'your Amazon 14-Week Quality Summary';
      }
    } else if (props.reportType === 'NinetyDayIdleTimeReport') {
      if (WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId)) {
        reportType = 'your Amazon 180-Day Idle Time Report';
      } else {
        reportType = 'your Amazon 90-Day Idle Time Report';
      }
    }
    let displayText = `Are you sure you want to email a copy of ${reportType} to your personal email on file with Amazon`;
    displayText = userDetails.emailAddress ? (displayText + ` (${userDetails.emailAddress})`) : displayText;
    displayText += '?';
    return (
        <ModalContent
            titleText="Send Email?"
            buttons={[
              <Button onClick={close}>
                No, Cancel
              </Button>,
              <Button onClick={() => sendEmail(close)} variant={ButtonVariant.Primary}>
                Yes, Send Email
              </Button>
            ]}>
          <Text>{displayText}</Text>
        </ModalContent>
    )
  };

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <>
          {sendEmailAttempted == false &&
            <Flex flexDirection="row" justifyContent="right" alignItems="center" gridGap="S300">
              <Button onClick={open} size={ButtonSize.Small}>
                Email Report <IconMail />
              </Button>
            </Flex>
          }
          {sendEmailInProgress &&
            <>
              <Spinner showText loadingText="Sending Email" />
            </>
          }
          {sendEmailSucceeded && successBanner}
          {sendEmailFailed && failureBanner}
        </>
      )}
    </WithModal>
  )
}
